import React, { useState } from 'react';
import { Search, Filter, ArrowUpDown, Download, Edit2, Clock, X, AlertTriangle, CheckCircle2, Upload } from 'lucide-react';
import { FB_ITEMS, FB_CATEGORIES, FB_AUDIT_LOG, FBItem } from '../data/mockData';
export function FBInventory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editQty, setEditQty] = useState('');
  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [selectedAuditItem, setSelectedAuditItem] = useState<FBItem | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  // Metrics
  const totalItems = FB_ITEMS.length;
  const inStock = FB_ITEMS.filter(i => i.status === 'IN_STOCK').length;
  const lowStock = FB_ITEMS.filter(i => i.status === 'LOW_STOCK').length;
  const outOfStock = FB_ITEMS.filter(i => i.status === 'OUT_OF_STOCK').length;
  // Filter and Sort
  let filteredItems = [...FB_ITEMS];
  if (statusFilter !== 'ALL') {
    filteredItems = filteredItems.filter(i => i.status === statusFilter);
  }
  if (searchQuery) {
    filteredItems = filteredItems.filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }
  // Default sort: Out of stock first, then low stock, then in stock
  filteredItems.sort((a, b) => {
    const statusWeight = {
      OUT_OF_STOCK: 0,
      LOW_STOCK: 1,
      IN_STOCK: 2
    };
    return statusWeight[a.status] - statusWeight[b.status];
  });
  const handleSaveQty = (id: string) => {
    // In a real app, this would update the backend
    setEditingRowId(null);
  };
  const openAuditLog = (item: FBItem) => {
    setSelectedAuditItem(item);
    setIsAuditOpen(true);
  };
  return <div className="flex flex-col gap-6 h-[calc(100vh-12rem)]" data-id="element-367">
      {/* Summary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-shrink-0" data-id="element-368">
        <div className="bg-white p-4 rounded-xl border border-gray-200 soft-shadow" data-id="element-369">
          <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-370">Total Items</p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-371">{totalItems}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-green-200 soft-shadow" data-id="element-372">
          <p className="text-sm font-medium text-green-600 mb-1" data-id="element-373">In Stock</p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-374">{inStock}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-amber-200 soft-shadow relative overflow-hidden" data-id="element-375">
          <div className="absolute top-0 right-0 w-16 h-16 bg-amber-50 rounded-bl-full -z-10" data-id="element-376" />
          <p className="text-sm font-medium text-amber-600 mb-1 flex items-center gap-1.5" data-id="element-377">
            <AlertTriangle className="w-4 h-4" data-id="element-378" /> Low Stock
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-379">{lowStock}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-red-200 soft-shadow relative overflow-hidden" data-id="element-380">
          <div className="absolute top-0 right-0 w-16 h-16 bg-red-50 rounded-bl-full -z-10" data-id="element-381" />
          <p className="text-sm font-medium text-red-600 mb-1" data-id="element-382">Out of Stock</p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-383">{outOfStock}</p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-white rounded-xl border border-gray-200 soft-shadow overflow-hidden min-h-0" data-id="element-384">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-4" data-id="element-385">
          <div className="flex items-center gap-3 flex-1 min-w-[300px]" data-id="element-386">
            <div className="relative flex-1 max-w-xs" data-id="element-387">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-388" />
              <input type="text" placeholder="Search items..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent outline-none" data-id="element-389" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="py-2 px-3 text-sm border border-gray-300 rounded-lg bg-white outline-none" data-id="element-390">
              <option value="ALL" data-id="element-391">All Status</option>
              <option value="IN_STOCK" data-id="element-392">In Stock</option>
              <option value="LOW_STOCK" data-id="element-393">Low Stock</option>
              <option value="OUT_OF_STOCK" data-id="element-394">Out of Stock</option>
            </select>
          </div>

          <button onClick={() => setIsImportOpen(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-395">
            <Upload className="w-4 h-4" data-id="element-396" />
            Bulk Import CSV
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto" data-id="element-397">
          <table className="w-full text-left border-collapse" data-id="element-398">
            <thead className="bg-white sticky top-0 z-10 shadow-sm" data-id="element-399">
              <tr data-id="element-400">
                <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-401">
                  Item
                </th>
                <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-402">
                  Category
                </th>
                <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-403">
                  Current Stock
                </th>
                <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-404">
                  Status
                </th>
                <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-405">
                  Last Updated
                </th>
                <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 text-right" data-id="element-406">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-407">
              {filteredItems.map(item => {
              const category = FB_CATEGORIES.find(c => c.id === item.categoryId)?.name;
              const isEditing = editingRowId === item.id;
              let rowBg = 'bg-white hover:bg-gray-50';
              if (item.status === 'OUT_OF_STOCK') rowBg = 'bg-red-50/50 hover:bg-red-50';
              if (item.status === 'LOW_STOCK') rowBg = 'bg-amber-50/50 hover:bg-amber-50';
              return <tr key={item.id} className={`transition-colors ${rowBg}`} data-id="element-408">
                    <td className="py-3 px-4" data-id="element-409">
                      <div className="flex items-center gap-3" data-id="element-410">
                        <div className="w-10 h-10 rounded bg-gray-200 flex-shrink-0" data-id="element-411" />
                        <div data-id="element-412">
                          <p className="font-medium text-gray-900" data-id="element-413">
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-414">₹{item.price}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600" data-id="element-415">
                      {category}
                    </td>
                    <td className="py-3 px-4" data-id="element-416">
                      {isEditing ? <div className="flex items-center gap-2" data-id="element-417">
                          <input type="number" value={editQty} onChange={e => setEditQty(e.target.value)} className="w-20 px-2 py-1 text-sm border border-gray-300 rounded" autoFocus data-id="element-418" />
                          <button onClick={() => handleSaveQty(item.id)} className="text-green-600 hover:bg-green-50 p-1 rounded" data-id="element-419">
                            <CheckCircle2 className="w-4 h-4" data-id="element-420" />
                          </button>
                          <button onClick={() => setEditingRowId(null)} className="text-gray-400 hover:bg-gray-100 p-1 rounded" data-id="element-421">
                            <X className="w-4 h-4" data-id="element-422" />
                          </button>
                        </div> : <div className="flex items-center gap-2" data-id="element-423">
                          <span className="font-semibold text-gray-900" data-id="element-424">
                            {item.currentStock}
                          </span>
                          <span className="text-xs text-gray-400" data-id="element-425">
                            / min {item.lowStockThreshold}
                          </span>
                        </div>}
                    </td>
                    <td className="py-3 px-4" data-id="element-426">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold ${item.status === 'IN_STOCK' ? 'bg-green-100 text-green-700' : item.status === 'LOW_STOCK' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-id="element-427">
                        {item.status === 'LOW_STOCK' && <AlertTriangle className="w-3 h-3 mr-1" data-id="element-428" />}
                        {item.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500" data-id="element-429">
                      {new Date(item.lastUpdated).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </td>
                    <td className="py-3 px-4 text-right" data-id="element-430">
                      <div className="flex items-center justify-end gap-2" data-id="element-431">
                        <button onClick={() => {
                      setEditingRowId(item.id);
                      setEditQty(String(item.currentStock));
                    }} className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors" title="Update Stock" data-id="element-432">
                          <Edit2 className="w-4 h-4" data-id="element-433" />
                        </button>
                        <button onClick={() => openAuditLog(item)} className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors" title="View Audit Log" data-id="element-434">
                          <Clock className="w-4 h-4" data-id="element-435" />
                        </button>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit Log Drawer */}
      {isAuditOpen && selectedAuditItem && <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setIsAuditOpen(false)} data-id="element-436" />
          <div className="fixed inset-y-0 right-0 w-full md:w-96 bg-white shadow-2xl z-50 flex flex-col" data-id="element-437">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-438">
              <div data-id="element-439">
                <h2 className="font-bold text-gray-900" data-id="element-440">Stock History</h2>
                <p className="text-sm text-gray-500" data-id="element-441">
                  {selectedAuditItem.name}
                </p>
              </div>
              <button onClick={() => setIsAuditOpen(false)} className="p-2 text-gray-400 hover:bg-gray-200 rounded-full" data-id="element-442">
                <X className="w-5 h-5" data-id="element-443" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6" data-id="element-444">
              <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent" data-id="element-445">
                {FB_AUDIT_LOG.filter(log => log.itemId === selectedAuditItem.id).map((log, idx) => <div key={log.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-446">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-gray-100 text-gray-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-447">
                      <Clock className="w-4 h-4" data-id="element-448" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-449">
                      <div className="flex items-center justify-between mb-1" data-id="element-450">
                        <span className="font-bold text-gray-900 text-sm" data-id="element-451">
                          {log.oldQty} → {log.newQty}
                        </span>
                        <span className="text-[10px] text-gray-500" data-id="element-452">
                          {new Date(log.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mb-2" data-id="element-453">{log.note}</p>
                      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider" data-id="element-454">
                        By {log.user}
                      </p>
                    </div>
                  </div>)}
                {FB_AUDIT_LOG.filter(log => log.itemId === selectedAuditItem.id).length === 0 && <div className="text-center text-gray-500 text-sm py-8" data-id="element-455">
                    No history available for this item.
                  </div>}
              </div>
            </div>
          </div>
        </>}

      {/* Bulk Import Modal */}
      {isImportOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-id="element-456">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden" data-id="element-457">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-id="element-458">
              <h2 className="font-bold text-gray-900" data-id="element-459">Bulk Import Inventory</h2>
              <button onClick={() => setIsImportOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-460">
                <X className="w-5 h-5" data-id="element-461" />
              </button>
            </div>
            <div className="p-6" data-id="element-462">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer mb-6" data-id="element-463">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" data-id="element-464" />
                <p className="text-sm font-medium text-gray-900" data-id="element-465">
                  Click to upload or drag CSV here
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-466">
                  Required columns: item_id, quantity, note
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg" data-id="element-467">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-468">
                  Instructions
                </h4>
                <ul className="text-xs text-blue-800 space-y-1 list-disc pl-4" data-id="element-469">
                  <li data-id="element-470">Download the current inventory template first</li>
                  <li data-id="element-471">Update the 'quantity' column with new stock levels</li>
                  <li data-id="element-472">Add an optional 'note' for the audit log</li>
                  <li data-id="element-473">Do not modify the 'item_id' column</li>
                </ul>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between" data-id="element-474">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-475">
                Download Template
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white rounded-lg opacity-50 cursor-not-allowed" style={{
            backgroundColor: 'var(--accent-color)'
          }} data-id="element-476">
                Import Data
              </button>
            </div>
          </div>
        </div>}
    </div>;
}